/**
 * Image processing utilities for e-ink display
 * Converts images to 6-color BMP format with various dithering algorithms
 */

import { findClosestColor } from './colorPalette'
import { DitherAlgorithm } from '../image-editor/useImageEditor'

// E-ink display dimensions (portrait orientation)
export const DISPLAY_WIDTH = 400
export const DISPLAY_HEIGHT = 600

export interface ProcessingOptions {
  width?: number
  height?: number
  ditherAlgorithm?: DitherAlgorithm
  fit?: 'scale' | 'stretch' | 'crop'
}

const DEFAULT_OPTIONS: ProcessingOptions = {
  width: DISPLAY_WIDTH,
  height: DISPLAY_HEIGHT,
  ditherAlgorithm: 'floydSteinberg',
  fit: 'scale',
}

/**
 * Load an image from a URL, File, or Blob
 */
export async function loadImage(source: string | File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))

    if (source instanceof Blob) {
      img.src = URL.createObjectURL(source)
    } else {
      img.src = source
    }
  })
}

/**
 * Resize image to fit display dimensions
 */
export function resizeImage(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  fit: 'scale' | 'stretch' | 'crop'
): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')!

  // Fill with white background
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, targetWidth, targetHeight)

  let sx = 0, sy = 0, sw = img.width, sh = img.height
  let dx = 0, dy = 0, dw = targetWidth, dh = targetHeight

  if (fit === 'scale') {
    // Scale to fit, maintaining aspect ratio, centered
    const scale = Math.min(targetWidth / img.width, targetHeight / img.height)
    dw = img.width * scale
    dh = img.height * scale
    dx = (targetWidth - dw) / 2
    dy = (targetHeight - dh) / 2
  } else if (fit === 'crop') {
    // Crop to fill, centered
    const scale = Math.max(targetWidth / img.width, targetHeight / img.height)
    sw = targetWidth / scale
    sh = targetHeight / scale
    sx = (img.width - sw) / 2
    sy = (img.height - sh) / 2
  }
  // 'stretch' uses default values (fills entire canvas)

  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)

  return ctx.getImageData(0, 0, targetWidth, targetHeight)
}

function clamp(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)))
}

/**
 * Apply dithering based on selected algorithm
 */
export function applyDithering(imageData: ImageData, algorithm: DitherAlgorithm = 'floydSteinberg'): ImageData {
  switch (algorithm) {
    case 'floydSteinberg':
      return ditherFloydSteinberg(imageData)
    case 'ordered':
      return ditherOrdered(imageData)
    case 'atkinson':
      return ditherAtkinson(imageData)
    case 'stucki':
      return ditherStucki(imageData)
    default:
      return ditherFloydSteinberg(imageData)
  }
}

/**
 * Floyd-Steinberg dithering
 * Classic error diffusion with 4 neighboring pixels
 * Distribution: 7/16, 3/16, 5/16, 1/16
 */
function ditherFloydSteinberg(imageData: ImageData): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const width = imageData.width
  const height = imageData.height

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4

      const oldR = data[idx]
      const oldG = data[idx + 1]
      const oldB = data[idx + 2]

      const newColor = findClosestColor(oldR, oldG, oldB)

      data[idx] = newColor.r
      data[idx + 1] = newColor.g
      data[idx + 2] = newColor.b

      const errR = oldR - newColor.r
      const errG = oldG - newColor.g
      const errB = oldB - newColor.b

      // Distribute error to neighboring pixels
      // Right pixel: 7/16
      if (x + 1 < width) {
        const i = idx + 4
        data[i] = clamp(data[i] + errR * 7 / 16)
        data[i + 1] = clamp(data[i + 1] + errG * 7 / 16)
        data[i + 2] = clamp(data[i + 2] + errB * 7 / 16)
      }

      // Bottom-left pixel: 3/16
      if (y + 1 < height && x > 0) {
        const i = ((y + 1) * width + x - 1) * 4
        data[i] = clamp(data[i] + errR * 3 / 16)
        data[i + 1] = clamp(data[i + 1] + errG * 3 / 16)
        data[i + 2] = clamp(data[i + 2] + errB * 3 / 16)
      }

      // Bottom pixel: 5/16
      if (y + 1 < height) {
        const i = ((y + 1) * width + x) * 4
        data[i] = clamp(data[i] + errR * 5 / 16)
        data[i + 1] = clamp(data[i + 1] + errG * 5 / 16)
        data[i + 2] = clamp(data[i + 2] + errB * 5 / 16)
      }

      // Bottom-right pixel: 1/16
      if (y + 1 < height && x + 1 < width) {
        const i = ((y + 1) * width + x + 1) * 4
        data[i] = clamp(data[i] + errR * 1 / 16)
        data[i + 1] = clamp(data[i + 1] + errG * 1 / 16)
        data[i + 2] = clamp(data[i + 2] + errB * 1 / 16)
      }
    }
  }

  return new ImageData(data, width, height)
}

/**
 * Atkinson dithering
 * Developed by Bill Atkinson for Mac. Diffuses only 3/4 of the error,
 * which tends to preserve detail better and works well for e-ink displays.
 * Distribution: 1/8 to 6 neighboring pixels (total 6/8 = 3/4)
 */
function ditherAtkinson(imageData: ImageData): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const width = imageData.width
  const height = imageData.height

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4

      const oldR = data[idx]
      const oldG = data[idx + 1]
      const oldB = data[idx + 2]

      const newColor = findClosestColor(oldR, oldG, oldB)

      data[idx] = newColor.r
      data[idx + 1] = newColor.g
      data[idx + 2] = newColor.b

      // Error divided by 8 (only spread 6/8 = 75% of error)
      const errR = (oldR - newColor.r) / 8
      const errG = (oldG - newColor.g) / 8
      const errB = (oldB - newColor.b) / 8

      // Atkinson pattern: spread to 6 pixels with 1/8 each
      const offsets = [
        [1, 0], [2, 0],           // Right, far right
        [-1, 1], [0, 1], [1, 1],  // Next row
        [0, 2]                     // Two rows down
      ]

      for (const [dx, dy] of offsets) {
        const nx = x + dx
        const ny = y + dy
        if (nx >= 0 && nx < width && ny < height) {
          const i = (ny * width + nx) * 4
          data[i] = clamp(data[i] + errR)
          data[i + 1] = clamp(data[i + 1] + errG)
          data[i + 2] = clamp(data[i + 2] + errB)
        }
      }
    }
  }

  return new ImageData(data, width, height)
}

/**
 * Ordered (Bayer) dithering
 * Uses a threshold matrix for very fast, patterned dithering.
 * Creates a characteristic crosshatch pattern.
 */
function ditherOrdered(imageData: ImageData): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const width = imageData.width
  const height = imageData.height

  // 8x8 Bayer matrix (normalized to 0-63 range, then scaled)
  const bayerMatrix = [
    [ 0, 32,  8, 40,  2, 34, 10, 42],
    [48, 16, 56, 24, 50, 18, 58, 26],
    [12, 44,  4, 36, 14, 46,  6, 38],
    [60, 28, 52, 20, 62, 30, 54, 22],
    [ 3, 35, 11, 43,  1, 33,  9, 41],
    [51, 19, 59, 27, 49, 17, 57, 25],
    [15, 47,  7, 39, 13, 45,  5, 37],
    [63, 31, 55, 23, 61, 29, 53, 21]
  ]

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4

      // Get threshold from Bayer matrix (scaled to -32 to +31 range)
      const threshold = (bayerMatrix[y % 8][x % 8] - 32) * 2

      // Add threshold to each channel before quantization
      const r = clamp(data[idx] + threshold)
      const g = clamp(data[idx + 1] + threshold)
      const b = clamp(data[idx + 2] + threshold)

      const newColor = findClosestColor(r, g, b)

      data[idx] = newColor.r
      data[idx + 1] = newColor.g
      data[idx + 2] = newColor.b
    }
  }

  return new ImageData(data, width, height)
}

/**
 * Stucki dithering
 * Higher quality error diffusion with larger kernel (12 pixels)
 * Produces smoother gradients than Floyd-Steinberg
 */
function ditherStucki(imageData: ImageData): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const width = imageData.width
  const height = imageData.height

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4

      const oldR = data[idx]
      const oldG = data[idx + 1]
      const oldB = data[idx + 2]

      const newColor = findClosestColor(oldR, oldG, oldB)

      data[idx] = newColor.r
      data[idx + 1] = newColor.g
      data[idx + 2] = newColor.b

      const errR = oldR - newColor.r
      const errG = oldG - newColor.g
      const errB = oldB - newColor.b

      // Stucki kernel (divisor = 42)
      // Current row:      *  8/42  4/42
      // Next row:    2/42  4/42  8/42  4/42  2/42
      // Row after:   1/42  2/42  4/42  2/42  1/42
      const kernel = [
        // [dx, dy, weight]
        [1, 0, 8], [2, 0, 4],
        [-2, 1, 2], [-1, 1, 4], [0, 1, 8], [1, 1, 4], [2, 1, 2],
        [-2, 2, 1], [-1, 2, 2], [0, 2, 4], [1, 2, 2], [2, 2, 1]
      ]

      for (const [dx, dy, weight] of kernel) {
        const nx = x + dx
        const ny = y + dy
        if (nx >= 0 && nx < width && ny < height) {
          const i = (ny * width + nx) * 4
          const factor = weight / 42
          data[i] = clamp(data[i] + errR * factor)
          data[i + 1] = clamp(data[i + 1] + errG * factor)
          data[i + 2] = clamp(data[i + 2] + errB * factor)
        }
      }
    }
  }

  return new ImageData(data, width, height)
}

/**
 * Quantize without dithering (simple nearest color)
 */
export function quantizeSimple(imageData: ImageData): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const width = imageData.width
  const height = imageData.height

  for (let i = 0; i < data.length; i += 4) {
    const color = findClosestColor(data[i], data[i + 1], data[i + 2])
    data[i] = color.r
    data[i + 1] = color.g
    data[i + 2] = color.b
  }

  return new ImageData(data, width, height)
}

/**
 * Find palette index for RGB values (after dithering, colors should be exact matches)
 */
function findPaletteIndexForRGB(r: number, g: number, b: number): number {
  if (r === 0 && g === 0 && b === 0) return 0        // Black
  if (r === 255 && g === 255 && b === 255) return 1  // White
  if (r === 255 && g === 255 && b === 0) return 2    // Yellow
  if (r === 255 && g === 0 && b === 0) return 3      // Red
  if (r === 0 && g === 0 && b === 255) return 4      // Blue
  if (r === 0 && g === 255 && b === 0) return 5      // Green
  return 0  // Default to black for any unexpected color
}

/**
 * Create a 4-bit indexed BMP file from ImageData
 * This is 6x smaller than 24-bit BMP and ESP32's drawBmp() natively supports it
 */
export function createBMP(imageData: ImageData): Uint8Array {
  const width = imageData.width
  const height = imageData.height
  const data = imageData.data

  // 4-bit BMP structure:
  // - BMP Header: 14 bytes
  // - DIB Header (BITMAPINFOHEADER): 40 bytes
  // - Color Palette: 16 colors × 4 bytes = 64 bytes
  // - Pixel Data: 2 pixels per byte, rows padded to 4 bytes

  const rowSize = Math.ceil(width / 2)  // bytes per row (2 pixels per byte)
  const paddedRowSize = Math.ceil(rowSize / 4) * 4  // Pad to 4 bytes
  const pixelDataSize = paddedRowSize * height
  const paletteSize = 16 * 4  // 16 colors × 4 bytes (BGRA)
  const headerSize = 14 + 40  // BMP header + DIB header
  const pixelOffset = headerSize + paletteSize
  const fileSize = pixelOffset + pixelDataSize

  const buffer = new ArrayBuffer(fileSize)
  const view = new DataView(buffer)
  const bytes = new Uint8Array(buffer)

  // === BMP Header (14 bytes) ===
  view.setUint16(0, 0x4D42, true)       // 'BM' signature
  view.setUint32(2, fileSize, true)     // File size
  view.setUint32(6, 0, true)            // Reserved
  view.setUint32(10, pixelOffset, true) // Pixel data offset

  // === DIB Header (BITMAPINFOHEADER - 40 bytes) ===
  view.setUint32(14, 40, true)          // Header size
  view.setInt32(18, width, true)        // Width
  view.setInt32(22, height, true)       // Height (positive = bottom-up)
  view.setUint16(26, 1, true)           // Color planes
  view.setUint16(28, 4, true)           // Bits per pixel (4-bit!)
  view.setUint32(30, 0, true)           // Compression (none)
  view.setUint32(34, pixelDataSize, true) // Image size
  view.setInt32(38, 2835, true)         // X pixels per meter
  view.setInt32(42, 2835, true)         // Y pixels per meter
  view.setUint32(46, 6, true)           // Colors used (6)
  view.setUint32(50, 6, true)           // Important colors (6)

  // === Color Palette (64 bytes for 16 colors, BGRA format) ===
  // Must match the order expected by findPaletteIndexForRGB()
  const palette = [
    [0, 0, 0],       // 0: Black (BGR)
    [255, 255, 255], // 1: White (BGR)
    [0, 255, 255],   // 2: Yellow (BGR = 0, 255, 255)
    [0, 0, 255],     // 3: Red (BGR = 0, 0, 255)
    [255, 0, 0],     // 4: Blue (BGR = 255, 0, 0)
    [0, 255, 0],     // 5: Green (BGR)
  ]

  let offset = headerSize
  for (let i = 0; i < 16; i++) {
    if (i < palette.length) {
      bytes[offset++] = palette[i][0]  // B
      bytes[offset++] = palette[i][1]  // G
      bytes[offset++] = palette[i][2]  // R
      bytes[offset++] = 0              // A (unused)
    } else {
      // Padding for unused palette entries
      bytes[offset++] = 0
      bytes[offset++] = 0
      bytes[offset++] = 0
      bytes[offset++] = 0
    }
  }

  // === Pixel Data ===
  // BMP is bottom-up, 2 pixels per byte
  // ESP32 expects: high nibble = even column, low nibble = odd column
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x += 2) {
      const idx1 = (y * width + x) * 4
      const idx2 = (y * width + x + 1) * 4

      // Get palette indices for both pixels
      const color1 = findPaletteIndexForRGB(data[idx1], data[idx1 + 1], data[idx1 + 2])
      const color2 = findPaletteIndexForRGB(data[idx2], data[idx2 + 1], data[idx2 + 2])

      // Pack: high nibble = first pixel (even), low nibble = second pixel (odd)
      bytes[offset++] = (color1 << 4) | color2
    }
    // Add padding to reach paddedRowSize
    const padding = paddedRowSize - rowSize
    for (let p = 0; p < padding; p++) {
      bytes[offset++] = 0
    }
  }

  console.log(`Created 4-bit BMP: ${fileSize} bytes (${(fileSize / 1024).toFixed(1)} KB)`)
  return new Uint8Array(buffer)
}

/**
 * Process an image for the e-ink display
 * Returns the processed BMP data
 */
export async function processImage(
  source: string | File | Blob,
  options: ProcessingOptions = {}
): Promise<Uint8Array> {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  // Load image
  const img = await loadImage(source)

  // Resize
  const resized = resizeImage(img, opts.width!, opts.height!, opts.fit!)

  // Apply dithering
  const processed = applyDithering(resized, opts.ditherAlgorithm)

  // Convert to BMP
  return createBMP(processed)
}

/**
 * Create a preview thumbnail
 */
export async function createThumbnail(
  source: string | File | Blob,
  maxSize: number = 200
): Promise<string> {
  const img = await loadImage(source)

  const scale = Math.min(maxSize / img.width, maxSize / img.height, 1)
  const width = Math.round(img.width * scale)
  const height = Math.round(img.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, width, height)

  return canvas.toDataURL('image/jpeg', 0.8)
}
